import React, { FC, useCallback, useMemo } from 'react';

import classNames from 'classnames';

import { EvSection, EvTitle } from '@evinced-private/ui-common';

import { MFA_DASHBOARD_PAGE_TABLE_SECTION_ID } from 'data-automation-ids/MfaDashboardAutomationIds';
import { IMfaSessionRow } from 'src/apps/mfa/types/MfaDashboardServiceTypes';
import { LabelsManagementTableActions } from 'src/common/components/labels-management-table-actions';
import { useMobileConfiguredValues } from 'src/common/components/labels-management-table-actions/hooks';
import { LABELS_ACTION_BUTTON_LABELS } from 'src/common/components/labels-management-table-actions/labels';
import { PlatformTable } from 'src/common/components/platform-table/PlatformTable';
import { MFA_SESSIONS_TABLE_ID } from 'src/common/pages/dashboards-container/dashboard_consts';

import { SELECT_SESSIONS_PROMPT } from './MfaSessions.constants';
import { IMfaDashboardTableProps } from './MfaSessions.types';
import { getMfaSessionsColumns } from './MfaSessionsTableHelper';

import './MfaSessionsTable.scss';

const TABLE_KEY_FIELD = 'sessionId';
const TABLE_TITLE = 'MFA Sessions';
const TABLE_CAPTION = 'MFA Sessions table';
const DATA_TYPE = 'sessions';

export const MfaSessionsTable: FC<IMfaDashboardTableProps> = ({
	sessionsQuery,
	unfilteredTotal,
	isBulkLabelEditEnabled,
	mobileCoreLabels,
	selectedRows,
	onSelectRow,
	onSelectAll,
	onClearSelection
}) => {
	const total: number = sessionsQuery.data?.total ?? 0;
	const { refetch } = sessionsQuery;
	const configuredValues = useMobileConfiguredValues();
	const columns = useMemo(
		() =>
			getMfaSessionsColumns(
				configuredValues.unitIdDisplayName,
				configuredValues.environmentDisplayName
			),
		[configuredValues.unitIdDisplayName, configuredValues.environmentDisplayName]
	);

	const isTableLoading =
		sessionsQuery.isLoading ||
		sessionsQuery.isPreviousData ||
		(sessionsQuery.isStale && sessionsQuery.isRefetching);

	const handleProcessingComplete = useCallback(async () => {
		await refetch();
		onClearSelection();
	}, [refetch, onClearSelection]);

	return (
		<EvSection
			ariaLabel="Mobile Flow Analyzer sessions table"
			skipLinkId="main-section"
			className={classNames('dashboard-section', 'mfa-sessions-table')}
			dataAutomationId={MFA_DASHBOARD_PAGE_TABLE_SECTION_ID}
		>
			<EvTitle titleText="Uploaded sessions" headingLevel={2} />

			{isBulkLabelEditEnabled && (
				<LabelsManagementTableActions<IMfaSessionRow>
					selectedRows={selectedRows}
					handleClearSelection={onClearSelection}
					promptText={SELECT_SESSIONS_PROMPT}
					itemName="session"
					addLabelButtonLabel={LABELS_ACTION_BUTTON_LABELS.ADD}
					changeLabelValueButtonLabel={LABELS_ACTION_BUTTON_LABELS.CHANGE}
					removeLabelsButtonLabel={LABELS_ACTION_BUTTON_LABELS.REMOVE}
					onLabelActionSuccess={handleProcessingComplete}
					tenantConfiguredLabels={mobileCoreLabels}
					configuredValues={configuredValues}
					appType="mfa"
				/>
			)}

			<PlatformTable
				isTableLoading={isTableLoading}
				tableId={MFA_SESSIONS_TABLE_ID}
				tableData={sessionsQuery.data?.rows}
				totalPaginationableResults={total}
				totalCount={unfilteredTotal}
				persistTableState
				columns={columns}
				options={{
					keyField: TABLE_KEY_FIELD,
					title: TABLE_TITLE,
					caption: TABLE_CAPTION,
					pagination: true,
					dataType: DATA_TYPE,
					remote: true,
					selectRow: isBulkLabelEditEnabled
						? {
								mode: 'checkbox',
								selected: selectedRows.map((r) => r.sessionId),
								onSelect: onSelectRow,
								onSelectAll
							}
						: undefined
				}}
			/>
		</EvSection>
	);
};
